import React, { useRef, useState } from "react";
import { Modal, Box, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import NeoButton from "./NeoButton";
import tokenImg from "../@shared/assets/infift_token.png";
import { dialogSurfaceSx } from "../@shared/ui/tokens";

const overlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  background:
    "radial-gradient(80% 60% at 50% 0%, rgba(124,77,255,0.28) 0%, rgba(0, 0, 0, 0.78) 70%, rgba(0,0,0,0.92) 100%)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1400,
};

const modalStyle = {
  width: "min(680px, 92vw)",
  maxHeight: "78dvh",
  padding: "10px 10px 30px",
  borderRadius: 12,
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  position: "relative",
  ...dialogSurfaceSx,
  borderRadius: 20,
};

const variants = {
  hidden: { scale: 0.98, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: { duration: 0.22, ease: "easeOut" },
  },
  exit: { scale: 0.98, opacity: 0, transition: { duration: 0.18 } },
};

const cardShell = {
  minWidth: 210,
  maxWidth: 210,
  minHeight: 248,
  borderRadius: 1.5,
  border: "1px solid rgba(255,255,255,0.10)",
  background:
    "linear-gradient(180deg, rgba(46, 26, 103, 0.82) 0%, rgba(9, 4, 25, 0.92) 100%)",
  boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08), 0 10px 20px rgba(0,0,0,0.35)",
  padding: 6,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 3,
  userSelect: "none",
};

export default function InfinityTokenPurchaseModal({
  visible,
  onClose,
  tokenBalance = 0,
  products = [],
  onBuy,
  onBattleWalletBuy,
  buyingKey = null,
  battleWalletBuyingKey = null,
  error = null,
}) {
  const list = Array.isArray(products) ? products : [];
  const scrollRef = useRef(null);
  const dragState = useRef({
    active: false,
    startX: 0,
    scrollLeft: 0,
    moved: false,
  });
  const [isDragging, setIsDragging] = useState(false);

  const handleWheel = (event) => {
    const el = scrollRef.current;
    if (!el) return;
    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY)
      ? event.deltaX
      : event.deltaY;
    if (!delta) return;
    event.preventDefault();
    el.scrollLeft += delta;
  };

  const handlePointerDown = (event) => {
    const el = scrollRef.current;
    if (!el || event.button > 0) return;
    if (
      event.target?.closest?.(
        "button, [role='button'], a, input, textarea, select"
      )
    ) {
      return;
    }
    dragState.current = {
      active: true,
      startX: event.clientX,
      scrollLeft: el.scrollLeft,
      moved: false,
    };
    el.setPointerCapture?.(event.pointerId);
    setIsDragging(true);
  };

  const handlePointerMove = (event) => {
    const el = scrollRef.current;
    const drag = dragState.current;
    if (!el || !drag.active) return;
    const dx = event.clientX - drag.startX;
    if (Math.abs(dx) > 4) drag.moved = true;
    el.scrollLeft = drag.scrollLeft - dx;
  };

  const stopDrag = (event) => {
    const el = scrollRef.current;
    if (el) el.releasePointerCapture?.(event.pointerId);
    dragState.current.active = false;
    setIsDragging(false);
  };

  const handleClickCapture = (event) => {
    if (!dragState.current.moved) return;
    event.preventDefault();
    event.stopPropagation();
    dragState.current.moved = false;
  };

  return (
    <Modal open={!!visible} onClose={onClose} closeAfterTransition>
      <AnimatePresence>
        {visible && (
          <Box
            component={motion.div}
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={variants}
            style={overlayStyle}
          >
            <Box style={modalStyle}>
              <Box
                sx={{
                  height: "100%",
                  minHeight: 0,
                  borderRadius: 2.5,
                  border: "1px solid rgba(255,255,255,0.10)",
                  background:
                    "radial-gradient(circle at top, #3b1f8a33 0%, transparent 55%)",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1,
                }}
              >
                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 900,
                    fontSize: { xs: 18, sm: 22 },
                    textShadow:
                      "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000",
                  }}
                >
                  Buy Infinity Tokens
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1.25,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 2,
                  background: "rgba(0,0,0,0.28)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <Box
                  component="img"
                  src={tokenImg}
                  alt=""
                  sx={{ width: 24, height: 24, objectFit: "contain" }}
                />
                <Typography
                  sx={{
                    color: "#e2d5ff",
                    fontWeight: 800,
                    fontSize: 14,
                  }}
                >
                  Current balance: {Number(tokenBalance || 0)}
                </Typography>
              </Box>

              <Box
                ref={scrollRef}
                onWheel={handleWheel}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={stopDrag}
                onPointerCancel={stopDrag}
                onPointerLeave={(event) => {
                  if (dragState.current.active) stopDrag(event);
                }}
                onClickCapture={handleClickCapture}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 1.5,
                  overflowX: "auto",
                  overflowY: "hidden",
                  pb: 1,
                  pr: 1,
                  cursor: isDragging ? "grabbing" : "grab",
                  scrollBehavior: isDragging ? "auto" : "smooth",
                  scrollbarWidth: "thin",
                  scrollbarColor: "rgba(213,0,249,0.8) rgba(255,255,255,0.08)",
                  touchAction: "pan-x",
                  "&::-webkit-scrollbar": {
                    height: 10,
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "rgba(255,255,255,0.08)",
                    borderRadius: 10,
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "rgba(213,0,249,0.75)",
                    borderRadius: 10,
                  },
                }}
              >
                {list.map((p) => {
                  const key = p?.id || p?.name;
                  const isBuying = buyingKey && key && buyingKey === key;
                  const isBattleWalletBuying =
                    battleWalletBuyingKey && key && battleWalletBuyingKey === key;
                  return (
                    <Box key={key} sx={cardShell}>
                      <Box
                        component="img"
                        src={p.image}
                        alt={p.name}
                        sx={{ width: 84, height: 84, objectFit: "contain" }}
                      />

                      <Typography
                        sx={{
                          color: "#fff",
                          fontWeight: 900,
                          textAlign: "center",
                          lineHeight: 1.15,
                          minHeight: 36,
                        }}
                      >
                        {p.name}
                      </Typography>

                      <Typography sx={{ color: "#cfc6ff", fontSize: 13 }}>
                        ${(Number(p.price || 0) * 0.01).toFixed(2)}
                      </Typography>

                      <Typography
                        sx={{
                          color: p.deal ? "#fee399" : "transparent",
                          visibility: p.deal ? "visible" : "hidden",
                          fontSize: 12,
                          fontWeight: 900,
                          mt: -0.5,
                          minHeight: 18,
                        }}
                      >
                        {p.deal ? "Best value" : "Best value"}
                      </Typography>

                      <Box
                        sx={{
                          mt: "auto",
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "stretch",
                          gap: 0.75,
                        }}
                      >
                        <NeoButton
                          color="#7c4dff"
                          text={isBuying ? "Buying..." : "BUY"}
                          width={100}
                          height={38}
                          textSize={12}
                          disabled={!!isBuying || !!isBattleWalletBuying}
                          onClick={() => onBuy?.(p)}
                        />

                        {onBattleWalletBuy && (
                          <NeoButton
                            color="#d62828"
                            text={
                              isBattleWalletBuying
                                ? "Processing..."
                                : "Battle Wallet"
                            }
                            width={100}
                            height={38}
                            textSize={8}
                            disabled={!!isBuying || !!isBattleWalletBuying}
                            onClick={() => onBattleWalletBuy?.(p)}
                          />
                        )}
                      </Box>
                    </Box>
                  );
                })}

                {!list.length && (
                  <Typography sx={{ color: "#cfc6ff" }}>
                    No token products available.
                  </Typography>
                )}
              </Box>

              {error && (
                <Typography sx={{ color: "#ff6b6b", fontSize: 12, mt: 1 }}>
                  {error}
                </Typography>
              )}

              <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: 12 }}>
                Purchases use the existing checkout flow.
              </Typography>
              </Box>

              <Box
                sx={{
                  position: "absolute",
                  bottom: "-18px",
                  left: 0,
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <NeoButton
                  isIcon
                  color="#d62828"
                  icon={<Close />}
                  width={46}
                  borderRadius={40}
                  onClick={() => onClose?.()}
                />
              </Box>
            </Box>
          </Box>
        )}
      </AnimatePresence>
    </Modal>
  );
}
